// --- React ---
import { useEffect, useState } from "react";

// --- Services ---
import { useUnsavedChangesWarning } from "../../../hooks/useUnsavedChangesWarning";
import stationSvc from "../../../services/api/station";

// --- Components ---
import ModalConfirm from "../../../components/modal-comfirm";
import FormInput from "../../../components/form-input/FormInput";
import { notify } from "../../../common/notificationService";
import { omitMetaFields } from "../../../utils/helper";
import FormDropzone from "../../../components/form-input/FormDropzone";

// --- Models ---
import { createStationSchema, updateStationSchema } from "../../../models/schema/station";
import { StationItem } from "../../../models/types/station";
import { FileItem } from "../../../models/types/common";

// --- Utils ---
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { LoaderCircle } from "lucide-react";
import FormFieldArray from "../../../components/form-input/FormFieldArray";
import { useTranslation } from "react-i18next";

interface FormProps {
  isCreate: boolean;
  data: StationItem | null;
  onSuccess: (id: string) => Promise<void>;
}

export default function StationCreateUpdate({ isCreate, data, onSuccess }: FormProps) {

  const { t } = useTranslation()
  const [loading, setLoading] = useState(false);
  const schema = isCreate ? createStationSchema : updateStationSchema;

  const {
    register,
    handleSubmit,
    reset,
    control,
    setValue,
    formState: { errors, isDirty }
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      name: '',
      code: '',
      address: '',
      longitude: 0,
      latitude: 0,
      type: '',
      dataLoggerAddress: '',
      smsWarning: [],
      emailWarning: [],
      telegramWarning: '',
      galaryFileIds: [],
      attachmentFileIds: [],
    },
  });

  useEffect(() => {
    if (data) {
      let galaryFileIds: number[] = [];
      if (data.images && data.images.length > 0) {
        galaryFileIds = data.images.map(f => Number(f.id));
      }
      let attachmentFileIds: number[] = [];
      if (data.attachments && data.attachments.length > 0) {
        attachmentFileIds = data.attachments.map(f => Number(f.id));
      }
      reset({ ...omitMetaFields(data, ['images', 'attachments']), galaryFileIds, attachmentFileIds });
    }
  }, [data, reset]);

  const onSubmit = async (values: any) => {
    setLoading(true);
    try {
      const res = isCreate ? await stationSvc.createStation(values) : await stationSvc.updateStation({ ...values, id: Number(data?.id) });
      if (res && res.content.status) {
        if (isCreate) {
          onSuccess(res.content.data.toString());
        }
        unlock();
        notify(t('common.success'), isCreate ? t('station.notification.createSuccess') : t('station.notifications.updateSuccess'), "success");
        setLoading(false);
      }
    } catch (e: any) {
      notify(t('common.error'), t('common.errorOccurred'), "danger");
    } finally {
      setLoading(false);
    }
  };

  const { showModal, confirmLeave, cancelLeave, confirmContent, unlock } = useUnsavedChangesWarning(isDirty);

  return (
    <>
      <ModalConfirm
        isOpen={showModal}
        onClose={cancelLeave}
        title={confirmContent.title}
        content={confirmContent.content}
        titleComfirm={t('common.leavePage')}
        action={confirmLeave}
      />
      <div className="w-full h-full">
        {/* <ButtonGoback /> */}
        <div className="mx-auto p-6 bg-white rounded-lg shadow">
          {/* <h2 className="text-2xl font-bold mb-6 text-center">{isCreate ? "Thêm tham số" : "Cập nhật tham số"}</h2> */}
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
              <FormInput
                label={t('station.form.fields.name')}
                name="name"
                register={register("name")}
                error={errors.name}
                required
              />
              <FormInput
                label={t('station.form.fields.code')}
                name="code"
                register={register("code")}
                error={errors.code}
                required
              />
              <FormInput
                label={t('station.form.fields.address')}
                name="address"
                register={register("address")}
                error={errors.address}
                required
              />
              <FormInput
                label={t('station.form.fields.longitude')}
                name="longitude"
                inputMode="decimal"
                register={register("longitude")}
                error={errors.longitude}
              />
              <FormInput
                label={t('station.form.fields.latitude')}
                name="latitude"
                inputMode="decimal"
                register={register("latitude")}
                error={errors.latitude}
              />
              <FormInput
                label={t('station.form.fields.type')}
                name="type"
                register={register("type")}
                error={errors.type}
              />
              <FormInput
                label={t('station.form.fields.dataLoggerAddress')}
                name="dataLoggerAddress"
                register={register("dataLoggerAddress")}
                error={errors.dataLoggerAddress}
              />
              <FormInput
                label={t('station.form.fields.telegramWarning')}
                name="telegramWarning"
                register={register("telegramWarning")}
                error={errors.telegramWarning}
              />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
              <div className="mt-4">
                <FormFieldArray
                  control={control}
                  name="smsWarning"
                  label={t('station.form.fields.smsWarning')}
                  errors={errors}
                  placeholder={t('station.form.placeholders.enterPhone')}
                />
              </div>
              <div className="mt-4">
                <FormFieldArray
                  control={control}
                  name="emailWarning"
                  label={t('station.form.fields.emailWarning')}
                  errors={errors}
                  placeholder={t('station.form.placeholders.enterEmail')}
                />
              </div>
            </div>
            {!isCreate ? (
              <>
                {data && (
                  <>
                    <div className="mt-4">
                      <FormDropzone
                        setValue={(files: FileItem[]) => setValue('galaryFileIds', files.map(f => Number(f.id)), { shouldDirty: true })}
                        label={t('station.form.fields.galleryImages')}
                        isPreview
                        multiple
                        accept={{
                          'image/*': ['.jpg', '.jpeg', '.png', '.gif', '.webp'],
                        }}
                        maxSize={10 * 1024 * 1024} // 10MB per file
                        helperText="Upload ảnh thư viện (max 10MB each)."
                        defaultValue={data.images}
                      />
                    </div>
                    {/* Upload attachmentFileIds */}
                    <div className="mt-4">
                      <FormDropzone
                        setValue={(files: FileItem[]) => setValue('attachmentFileIds', files.map(f => Number(f.id)), { shouldDirty: true })}
                        label={t('station.form.fields.attachments')}
                        multiple
                        accept={{
                          'application/pdf': ['.pdf'],
                          'application/msword': ['.doc'],
                          'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
                        }}
                        maxSize={10 * 1024 * 1024} // 10MB per file
                        helperText="Upload PDF or Word documents (max 10MB each)."
                        defaultValue={data.attachments}
                      />
                    </div>
                  </>
                )}
              </>
            ) : (<>
              <div className="mt-4">
                <FormDropzone
                  setValue={(files: FileItem[]) => setValue('galaryFileIds', files.map(f => Number(f.id)), { shouldDirty: true })}
                  label="Ảnh thư viện"
                  isPreview
                  multiple
                  accept={{
                    'image/*': ['.jpg', '.jpeg', '.png', '.gif', '.webp'],
                  }}
                  maxSize={10 * 1024 * 1024} // 10MB per file
                  helperText="Upload ảnh thư viện (max 10MB each)."
                  defaultValue={[]}
                />
              </div>
              {/* Upload attachmentFileIds */}
              <div className="mt-4">
                <FormDropzone
                  setValue={(files: FileItem[]) => setValue('attachmentFileIds', files.map(f => Number(f.id)), { shouldDirty: true })}
                  label="Tệp đính kèm"
                  multiple
                  accept={{
                    'application/pdf': ['.pdf'],
                    'application/msword': ['.doc'],
                    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
                  }}
                  maxSize={10 * 1024 * 1024} // 10MB per file
                  helperText="Upload PDF or Word documents (max 10MB each)."
                  defaultValue={[]}
                />
              </div>
            </>)}
            <button
              disabled={!isDirty}
              type="submit"
              className="btn_submit mx-auto mt-4"
            >
              <div className="flex items-center justify-center gap-2">
                {loading ? <LoaderCircle /> : ""}
                {t('station.form.buttons.save')}
              </div>
            </button>
          </form>
        </div>
      </div>
    </>
  );
}